import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { alertsService, AlertFilters } from '@/services/alerts';
import { AlertType } from '@/types';

interface UseAlertsOptions extends AlertFilters {
  enabled?: boolean;
}

export function useAlerts(options?: UseAlertsOptions) {
  const { enabled = true, ...filters } = options || {};
  return useQuery({
    queryKey: ['alerts', filters],
    queryFn: () => alertsService.getAlerts(filters),
    enabled,
  });
}

interface UseAlertCountsOptions {
  enabled?: boolean;
}

export function useAlertCounts(options?: UseAlertCountsOptions) {
  const { enabled = true } = options || {};
  return useQuery({
    queryKey: ['alerts', 'counts'],
    queryFn: alertsService.getAlertCounts,
    refetchInterval: 60000, // Refrescar cada minuto
    enabled,
  });
}

export function useDismissAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => alertsService.dismissAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

export function useMarkAlertRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => alertsService.updateAlert(id, { is_read: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

export function useMarkAllAlertsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alertType?: AlertType) => alertsService.markAllAsRead(alertType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

export function useGenerateAlerts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: alertsService.generateAlerts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}
